// UTM + segment-hint passthrough for the intake flow.
//
// Space & Time runs ads at apply.enrollment.leap21.ai/welcome with UTM
// parameters identifying the campaign + creative + segment intent. We
// stash them in sessionStorage on first paint and re-emit them on the
// final POST /intake/vesper5/complete so the worker can stamp the
// registry row with the original source.
//
// Why sessionStorage: localStorage outlives browser sessions and would
// taint future un-attributed visits; URL params alone don't survive
// multi-page React Router navigation. Session storage is the right TTL.

// gitleaks false-positive — this is a sessionStorage namespace, not a credential.
const SESSION_KEY = "leap21.intake.attribution.v1"; // gitleaks:allow

export interface IntakeAttribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  /** Hint from the ad — "glp1rec" routes to the GLP-1 cohort intake variant. */
  seg?: string;
  /** ms epoch when the visit landed; used by the worker for click-window joins. */
  landedAt: number;
}

const ALLOWED_KEYS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "seg",
]);

const VALUE_PATTERN = /^[A-Za-z0-9._~%-]{1,128}$/;

/** Capture UTM + seg from the current URL into session storage if present.
 *  Idempotent: subsequent calls with no UTMs do not overwrite an existing
 *  attribution (so navigating to /prescreen doesn't drop the welcome-page UTMs).
 *  Returns the stored attribution or null if none. */
export function captureAttributionFromUrl(): IntakeAttribution | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const incoming: Partial<IntakeAttribution> = {};
  for (const [k, v] of params.entries()) {
    if (!ALLOWED_KEYS.has(k)) continue;
    if (!VALUE_PATTERN.test(v)) continue;
    if (k === "utm_source") incoming.utmSource = v;
    else if (k === "utm_medium") incoming.utmMedium = v;
    else if (k === "utm_campaign") incoming.utmCampaign = v;
    else if (k === "utm_content") incoming.utmContent = v;
    else if (k === "utm_term") incoming.utmTerm = v;
    else if (k === "seg") incoming.seg = v;
  }
  const hasIncoming = Object.keys(incoming).length > 0;
  const existing = readAttribution();
  if (hasIncoming) {
    const next: IntakeAttribution = {
      ...(existing ?? {}),
      ...incoming,
      landedAt: existing?.landedAt ?? Date.now(),
    };
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
    } catch (e) {
      // sessionStorage may be unavailable (Safari private mode, quota
      // exceeded). The browser still works without attribution; warn so
      // a sustained failure rate is visible in console-error sampling.
      console.warn("[attribution] sessionStorage write failed", e);
    }
    return next;
  }
  return existing;
}

export function readAttribution(): IntakeAttribution | null {
  if (typeof window === "undefined") return null;
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SESSION_KEY);
  } catch (e) {
    // sessionStorage unreachable (rare); behave like "no attribution".
    console.warn("[attribution] sessionStorage read failed", e);
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as IntakeAttribution;
    if (typeof parsed?.landedAt !== "number") {
      // Schema mismatch (e.g. extension-tampered, version skew).
      // Clear the poison so subsequent reads aren't permanently broken.
      console.warn("[attribution] sessionStorage payload missing landedAt — clearing");
      clearAttribution();
      return null;
    }
    return parsed;
  } catch (e) {
    // Corrupted JSON (extension tampering, partial mid-write quota
    // exception). Clear it so we don't fail every read forever.
    console.warn("[attribution] sessionStorage payload corrupted — clearing", e);
    clearAttribution();
    return null;
  }
}

export function clearAttribution(): void {
  if (typeof window === "undefined") return;
  try { window.sessionStorage.removeItem(SESSION_KEY); } catch { /* noop */ }
}
